/**
 * The schema's filter groups. Groups the schema marks `defaultOpen` render as
 * plain sections; the rest ("advanced") are collapsed, and each collapsed
 * header says what's set inside it so nothing doing work is hidden. A group
 * holding an error is held open.
 */
import { useState, type JSX, type ReactNode } from 'react';
import { ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { SearchFilterDef, SearchGroupDef, SearchParamValue, SearchSchema } from '../api/types';
import { CountBadge } from './controls';
import { FilterControl, RangeControl, rangeLabel } from './FilterControl';
import { isVisible, type ActiveFilterInfo, type SearchFormState } from './params';

export interface FilterGroupsProps {
  schema: SearchSchema;
  state: SearchFormState;
  onChange: (key: string, v: SearchParamValue) => void;
  errors: Record<string, string>;
  active: ActiveFilterInfo[];
  forceOpen: Set<string>;
  disabled?: Record<string, string>;
  addons?: Record<string, ReactNode>;
  /** Groups not rendered here (e.g. rendered beside the query box). */
  skip?: string[];
}

type Item =
  | { kind: 'one'; def: SearchFilterDef }
  | { kind: 'range'; min: SearchFilterDef; max: SearchFilterDef };

/** Visible filters of a group, with adjacent min/max pairs folded into ranges. */
function itemsFor(schema: SearchSchema, group: string, state: SearchFormState): Item[] {
  const defs = schema.filters.filter((f) => f.group === group && isVisible(f, state));
  const items: Item[] = [];
  for (let i = 0; i < defs.length; i++) {
    const def = defs[i]!;
    const next = defs[i + 1];
    if (def.key.startsWith('min') && next && next.key === `max${def.key.slice(3)}` && def.type === next.type) {
      items.push({ kind: 'range', min: def, max: next });
      i++;
    } else {
      items.push({ kind: 'one', def });
    }
  }
  return items;
}

const WIDE = new Set(['countryList', 'languageList', 'text']);

export function GroupFields({
  schema,
  group,
  state,
  onChange,
  errors,
  disabled,
  addons,
}: Omit<FilterGroupsProps, 'active' | 'forceOpen' | 'skip'> & { group: string }): JSX.Element | null {
  const items = itemsFor(schema, group, state);
  if (items.length === 0) return null;
  const fields = items.filter((it) => it.kind === 'range' || it.def.type !== 'boolean');
  const toggles = items.filter((it): it is { kind: 'one'; def: SearchFilterDef } => it.kind === 'one' && it.def.type === 'boolean');
  return (
    <div className="space-y-4">
      {fields.length > 0 && (
        <div className="grid gap-x-4 gap-y-4 sm:grid-cols-2 xl:grid-cols-3">
          {fields.map((it) =>
            it.kind === 'range' ? (
              <RangeControl
                key={it.min.key}
                label={rangeLabel(it.min)}
                min={it.min}
                max={it.max}
                values={state.params}
                onChange={onChange}
                errors={errors}
              />
            ) : (
              <div key={it.def.key} className={cn(WIDE.has(it.def.type) && 'sm:col-span-2')}>
                <FilterControl
                  def={it.def}
                  value={state.params[it.def.key]}
                  onChange={(v) => onChange(it.def.key, v)}
                  error={errors[it.def.key]}
                  disabledReason={disabled?.[it.def.key]}
                  addon={addons?.[it.def.key]}
                />
              </div>
            ),
          )}
        </div>
      )}
      {toggles.length > 0 && (
        <div className="grid gap-3 md:grid-cols-2">
          {toggles.map(({ def }) => (
            <div key={def.key} className={cn(addons?.[def.key] && 'md:col-span-2')}>
              <FilterControl
                def={def}
                value={state.params[def.key]}
                onChange={(v) => onChange(def.key, v)}
                error={errors[def.key]}
                disabledReason={disabled?.[def.key]}
                addon={addons?.[def.key]}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function FilterGroups(props: FilterGroupsProps): JSX.Element {
  const { schema, state, active, forceOpen, skip = [] } = props;
  const groups = schema.groups.filter((g) => !skip.includes(g.id) && itemsFor(schema, g.id, state).length > 0);
  const open = groups.filter((g) => g.defaultOpen);
  const advanced = groups.filter((g) => !g.defaultOpen);
  return (
    <div className="space-y-5">
      {open.map((g) => (
        <section key={g.id} aria-labelledby={`search-group-${g.id}`} className="space-y-3">
          <h3 id={`search-group-${g.id}`} className="text-sm font-semibold">
            {g.label}
          </h3>
          <GroupFields {...props} group={g.id} />
        </section>
      ))}
      {advanced.length > 0 && (
        <div className="space-y-2">
          {advanced.map((g) => (
            <CollapsibleGroup
              key={g.id}
              group={g}
              active={active.filter((a) => a.group === g.id)}
              forceOpen={forceOpen.has(g.id)}
            >
              <GroupFields {...props} group={g.id} />
            </CollapsibleGroup>
          ))}
        </div>
      )}
    </div>
  );
}

function CollapsibleGroup({
  group,
  active,
  forceOpen,
  children,
}: {
  group: SearchGroupDef;
  active: ActiveFilterInfo[];
  forceOpen: boolean;
  children: ReactNode;
}): JSX.Element {
  const [open, setOpen] = useState(false);
  const isOpen = open || forceOpen;
  const summary = active.map((a) => a.text).join(' · ') || 'Defaults';
  return (
    <div className={cn('rounded-lg border bg-card', active.length > 0 ? 'border-brand/35' : 'border-border')}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={isOpen}
        className="flex w-full items-center gap-2.5 rounded-lg px-3 py-2.5 text-left transition-colors hover:bg-accent/40"
      >
        <span className="shrink-0 text-sm font-semibold">{group.label}</span>
        <CountBadge n={active.length} />
        <span className={cn('min-w-0 flex-1 truncate text-sm', active.length > 0 ? 'text-foreground/80' : 'text-muted-foreground')}>
          {summary}
        </span>
        <ChevronDown
          className={cn('h-4 w-4 shrink-0 text-muted-foreground transition-transform duration-150', isOpen && 'rotate-180')}
          strokeWidth={2}
        />
      </button>
      {isOpen && <div className="border-t border-border p-3 sm:p-4">{children}</div>}
    </div>
  );
}
